import {
  type CloserStatus,
  type PermissionType,
  TERROS_COMMON_PERMISSIONS,
  type UserData,
  type UserId,
  type UserProfileData,
  isSystemUser,
  isUserId,
  toTinyUser,
} from '../model'
import { pickAuditProps } from './audit'
import { isEmpty } from './isEmpty'

export function toUserData(profile: UserProfileData, addAgreements?: boolean): UserData {
  return {
    ...toTinyUser(profile),
    ...pickAuditProps(profile),
    email: profile.email,
    companyId: profile.companyId,
    isDeleted: profile.isDeleted,
    firstAccess: profile.firstAccess,
    phone: profile.phone,
    startDate: profile.startDate,
    language: profile.language,
    timeZone: profile.timeZone,
    clientUserId: profile.clientUserId,
    title: profile.title,
    teams: profile.teams,
    roles: profile.roles,
    closerStatus: profile.closerStatus,
    biography: profile.biography,
    color: profile.color,
    customFields: profile.customFields,
    managerId: profile.managerId,
    hasManager: profile.hasManager ?? true,
    selfReportedManager: profile.selfReportedManager,
    agreeToAITraining: addAgreements ? profile.agreeToAITraining : undefined,
    agreeToLicensing: addAgreements ? profile.agreeToLicensing : undefined,
    agreeToTerms: addAgreements ? profile.agreeToTerms : undefined,
  }
}

export function isRealUserId(userId?: string): userId is UserId {
  if (isEmpty(userId)) return false
  if (!isUserId(userId)) return false
  if (isSystemUser(userId)) return false
  return true
}

export function canOnlySet(closerStatus?: CloserStatus): boolean {
  if (isEmpty(closerStatus)) return false
  return closerStatus === 'Needs_Help'
}

export const SETTER_STATUSES: CloserStatus[] = ['Needs_Help', 'Can_Close', 'Can_Help'] as const

export function canSet(closerStatus?: CloserStatus): boolean {
  if (isEmpty(closerStatus)) return false
  return SETTER_STATUSES.includes(closerStatus)
}

export const CLOSING_STATUSES: CloserStatus[] = ['Can_Close', 'Closer_Only', 'Can_Help']

export function canClose(closerStatus?: CloserStatus): boolean {
  if (isEmpty(closerStatus)) return false
  return CLOSING_STATUSES.includes(closerStatus)
}

export function canHelp(closerStatus?: CloserStatus): boolean {
  if (isEmpty(closerStatus)) return false
  const helpStatuses: CloserStatus[] = ['Closer_Only', 'Can_Help']
  return helpStatuses.includes(closerStatus)
}

export function canSetAndClose(closerStatus?: CloserStatus): boolean {
  if (isEmpty(closerStatus)) return false
  const setAndCloseStatuses: CloserStatus[] = ['Can_Close', 'Can_Help']
  return setAndCloseStatuses.includes(closerStatus)
}

export function getDialect(user: Pick<UserData, 'companyId' | 'language'>): string {
  const { companyId, language = 'en' } = user
  return `${language}-${companyId}`
}

/** checks for permission on any team */
export function hasUserPermission(
  user: Pick<UserProfileData, 'permissionMap'> | undefined,
  permissionType: PermissionType,
  isTantalim?: boolean
): boolean {
  if (!user) return false
  const scopes = user.permissionMap?.[permissionType]
  if (isTantalim && TERROS_COMMON_PERMISSIONS.includes(permissionType)) return true
  if (isEmpty(scopes)) return false
  return scopes.some((s) => s.entity !== 'none')
}
